import * as THREE from "three";

/**
 * The controls for the item in hand, drawn in the world rather than in a panel.
 *
 * A toolbar puts a strip of chrome between you and the thing you are arranging, and on a narrow
 * screen it sits directly on top of it. These sit on the item instead: turn it with the rotate
 * grip, pull the corner grip out to make it bigger, tap the bin to put it away.
 *
 * The icons are drawn here rather than shipped as images — three small glyphs are cheaper to draw
 * than to load, and they stay crisp because the sprite is redrawn at whatever size it is used.
 */

export type HandleKind = "rotate" | "scale" | "delete";

/** How big a grip is on screen, as a fraction of viewport height, whatever the zoom. */
const SCREEN_FRACTION = 0.055;
/**
 * Where each grip sits around the item, as a clock position in radians.
 *
 * Spread across a third of a turn rather than a quarter. At the old spacing three grips sat 45°
 * apart on an orbit barely wider than a grip, so their edges touched and picking the middle one
 * meant aiming at a gap between two others.
 */
const ANGLES: Record<HandleKind, number> = {
  rotate: Math.PI * 0.83,
  delete: Math.PI * 0.5,
  scale: Math.PI * 0.17
};

function glyph(draw: (context: CanvasRenderingContext2D, size: number) => void): THREE.Texture {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext("2d")!;

  // A soft disc so the grip reads against grass, stone or water alike.
  context.beginPath();
  context.arc(size / 2, size / 2, size / 2 - 4, 0, Math.PI * 2);
  context.fillStyle = "#f6eedc";
  context.fill();
  context.lineWidth = 5;
  context.strokeStyle = "#2e3a2c";
  context.stroke();

  context.strokeStyle = "#2e3a2c";
  context.fillStyle = "#2e3a2c";
  context.lineWidth = 8;
  context.lineCap = "round";
  context.lineJoin = "round";
  draw(context, size);

  const texture = new THREE.CanvasTexture(canvas);
  texture.anisotropy = 4;
  return texture;
}

/** A circular arrow, for turning. */
const rotateGlyph = () => glyph((context, size) => {
  const centre = size / 2;
  const radius = size * 0.24;
  context.beginPath();
  context.arc(centre, centre, radius, Math.PI * 0.35, Math.PI * 1.75);
  context.stroke();
  const tipX = centre + Math.cos(Math.PI * 0.35) * radius;
  const tipY = centre + Math.sin(Math.PI * 0.35) * radius;
  context.beginPath();
  context.moveTo(tipX - 13, tipY - 5);
  context.lineTo(tipX + 4, tipY + 3);
  context.lineTo(tipX - 6, tipY + 16);
  context.closePath();
  context.fill();
});

/** A diagonal double arrow, for pulling bigger or smaller. */
const scaleGlyph = () => glyph((context, size) => {
  const a = size * 0.33;
  const b = size * 0.67;
  context.beginPath();
  context.moveTo(a, b);
  context.lineTo(b, a);
  context.stroke();
  for (const [x, y, dx, dy] of [[b, a, -1, 1], [a, b, 1, -1]] as const) {
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x + dx * 22, y + dy * 4);
    context.moveTo(x, y);
    context.lineTo(x + dx * 4, y + dy * 22);
    context.stroke();
  }
});

/** A bin, for putting the item back in the collection. */
const deleteGlyph = () => glyph((context, size) => {
  const centre = size / 2;
  context.lineWidth = 7;
  context.beginPath();
  context.moveTo(centre - 22, centre - 14);
  context.lineTo(centre + 22, centre - 14);
  context.stroke();
  context.beginPath();
  context.moveTo(centre - 8, centre - 22);
  context.lineTo(centre + 8, centre - 22);
  context.stroke();
  context.beginPath();
  context.moveTo(centre - 16, centre - 8);
  context.lineTo(centre - 12, centre + 24);
  context.lineTo(centre + 12, centre + 24);
  context.lineTo(centre + 16, centre - 8);
  context.stroke();
});

export class PlacementHandles {
  private readonly group = new THREE.Group();
  private readonly sprites = new Map<HandleKind, THREE.Sprite>();
  /** Where the grips orbit, in world space, and how far out they sit. */
  private anchor = new THREE.Vector3();
  private spread = 1;

  constructor(scene: THREE.Scene) {
    const glyphs: Record<HandleKind, () => THREE.Texture> = {
      rotate: rotateGlyph,
      scale: scaleGlyph,
      delete: deleteGlyph
    };

    for (const kind of Object.keys(glyphs) as HandleKind[]) {
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
        map: glyphs[kind](),
        transparent: true,
        // Grips must stay reachable even when the item they belong to is in front of them.
        depthTest: false,
        depthWrite: false
      }));
      sprite.userData.handleKind = kind;
      sprite.renderOrder = 10;
      this.sprites.set(kind, sprite);
      this.group.add(sprite);
    }

    this.group.visible = false;
    scene.add(this.group);
  }

  /** Puts the grips around a point, sized to clear an item of the given radius. */
  public show(anchor: THREE.Vector3, itemRadius: number): void {
    this.anchor.copy(anchor);
    this.spread = Math.max(0.8, itemRadius + 0.5);
    this.group.visible = true;
  }

  public hide(): void {
    this.group.visible = false;
  }

  public get visible(): boolean {
    return this.group.visible;
  }

  public get pickables(): THREE.Object3D[] {
    return this.group.visible ? [...this.sprites.values()] : [];
  }

  public kindOf(object: THREE.Object3D): HandleKind | null {
    const kind = object.userData?.handleKind;
    return kind === "rotate" || kind === "scale" || kind === "delete" ? kind : null;
  }

  /** Screen position of a grip, for measuring a drag against it. */
  public screenPositionOf(kind: HandleKind, camera: THREE.Camera, rect: DOMRect): { x: number; y: number } | null {
    const sprite = this.sprites.get(kind);
    if (!sprite || !this.group.visible) return null;
    const projected = sprite.getWorldPosition(new THREE.Vector3()).project(camera);
    return {
      x: rect.left + ((projected.x + 1) / 2) * rect.width,
      y: rect.top + ((-projected.y + 1) / 2) * rect.height
    };
  }

  /**
   * Keeps the grips facing the camera, the same size on screen at any zoom, and arranged around
   * the item rather than lost behind it. Called once a frame.
   */
  public update(camera: THREE.PerspectiveCamera, viewportHeight: number): void {
    if (!this.group.visible) return;

    const distance = camera.position.distanceTo(this.anchor);
    // Apparent size is scale over distance, so scaling with distance holds it steady on screen.
    const worldPerPixel = (2 * Math.tan((camera.fov * Math.PI) / 180 / 2) * distance) / viewportHeight;
    const size = worldPerPixel * viewportHeight * SCREEN_FRACTION;

    // Arrange them in the plane facing the camera, so they never overlap the item or each other
    // whichever way the village is turned.
    const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
    const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1);
    // Far enough out that neighbouring grips clear each other at the angles above, and far enough
    // from the item that they never sit on top of the thing they are adjusting.
    const orbit = Math.max(this.spread, size * 2.1);

    for (const [kind, sprite] of this.sprites) {
      const angle = ANGLES[kind];
      sprite.position
        .copy(this.anchor)
        .addScaledVector(right, Math.cos(angle) * orbit)
        .addScaledVector(up, Math.sin(angle) * orbit);
      sprite.scale.setScalar(size);
    }
  }

  public destroy(scene: THREE.Scene): void {
    for (const sprite of this.sprites.values()) {
      sprite.material.map?.dispose();
      sprite.material.dispose();
    }
    this.sprites.clear();
    scene.remove(this.group);
  }
}
